import React, { useEffect, useState } from 'react';

import { RiAddCircleLine } from '@remixicon/react';

import { Banner } from '@/design-system/banner';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';
import { Textarea } from '@/design-system/textarea';
import {
  ujpAmendmentErrorCode,
  ujpAPI,
  type CreateUjpAmendmentResult,
} from '@/services/api/ujp';

import { UJP_COPY } from '../copy';
import { toNum } from '../format';
import MoneyInput from './MoneyInput';

/**
 * "Ajukan biaya tambahan" (CR-7) — a nominal and a justification on an
 * APPROVED UJP. The modal owns the POST (like the wizard owns create) so the
 * 409 rule codes (`UJP_NOT_APPROVED` / `SHIPMENT_COMPLETED` /
 * `AMENDMENT_PENDING`) can be shown inline where the operator is looking,
 * then hands the created row back through `onCreated` so the panel refetches.
 *
 * Client-side checks mirror the DTO (CR7-D4): amount > 0, justifikasi >= 3
 * characters. Money stays a digits string; the server owns the arithmetic.
 */

const JUSTIFICATION_MIN = 3;

export interface UjpAmendmentModalProps {
  open: boolean;
  ujpId: string;
  referenceId: string;
  onClose: () => void;
  onCreated: (result: CreateUjpAmendmentResult) => void;
}

const UjpAmendmentModal: React.FC<UjpAmendmentModalProps> = ({
  open,
  ujpId,
  referenceId,
  onClose,
  onCreated,
}) => {
  const c = UJP_COPY.amendment;
  const [amount, setAmount] = useState('');
  const [justifikasi, setJustifikasi] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setAmount('');
      setJustifikasi('');
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const amountInvalid = toNum(amount) <= 0;
  const justificationInvalid = justifikasi.trim().length < JUSTIFICATION_MIN;
  const canSubmit = !amountInvalid && !justificationInvalid && !submitting;

  const submit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ujpAPI.createAmendment(ujpId, {
        amount,
        justifikasi: justifikasi.trim(),
      });
      onCreated(res);
    } catch (err) {
      const code = ujpAmendmentErrorCode(err);
      setError(
        code
          ? c.errors[code]
          : err instanceof Error
            ? err.message
            : UJP_COPY.toast.amendmentSubmitFailed
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      icon={<RiAddCircleLine />}
      iconTone="primary"
      title={c.createTitle(referenceId)}
      subtitle={c.createSubtitle}
      size="sm"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={submitting}
          >
            {UJP_COPY.wizard.cancel}
          </Button>
          <Button onClick={submit} loading={submitting} disabled={!canSubmit}>
            {submitting ? c.submitting : c.submit}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {error && (
          <Banner status="error" title={UJP_COPY.toast.amendmentSubmitFailed}>
            {error}
          </Banner>
        )}

        <Field>
          <Label htmlFor="ujp-amendment-amount" required>
            {c.amount}
          </Label>
          <MoneyInput
            id="ujp-amendment-amount"
            aria-label={c.amount}
            value={amount}
            onChange={(v) => {
              setError(null);
              setAmount(v);
            }}
            invalid={touched && amountInvalid}
            disabled={submitting}
            autoFocus
          />
          {touched && amountInvalid && (
            <Hint tone="error">{c.validation.amount}</Hint>
          )}
        </Field>

        <Field>
          <Label htmlFor="ujp-amendment-justifikasi" required>
            {c.justification}
          </Label>
          <Textarea
            id="ujp-amendment-justifikasi"
            value={justifikasi}
            onChange={(e) => {
              setError(null);
              setJustifikasi(e.target.value);
            }}
            rows={3}
            maxLength={1000}
            placeholder={c.justificationPlaceholder}
            invalid={touched && justificationInvalid}
            disabled={submitting}
            className="resize-none"
          />
          {touched && justificationInvalid && (
            <Hint tone="error">{c.validation.justification}</Hint>
          )}
        </Field>
      </form>
    </FormModal>
  );
};

export default UjpAmendmentModal;
